import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTutorialContext } from '@/contexts/TutorialContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { getUserId } from '../../../utils/auth';
import {
  ANALYTICS_EVENT_ACTIONS,
  ANALYTICS_EVENTS,
  CCUS_PATH,
  COUNTRY_OVERVIEWS_PATH,
  GHG_PATH,
  GLOBAL_DOWNLOADS_PATH,
  M_A_OPPORTUNITIES_ANALYTICS_PATH,
  M_A_OPPORTUNITIES_TABLE_PATH,
  M_A_TRANSACTIONS_ANALYTICS_PATH,
  M_A_TRANSACTIONS_COMPANY_ANALYSIS_PATH,
  M_A_TRANSACTIONS_OVERVIEW_PATH,
  M_A_TRANSACTIONS_TABLE_PATH,
  OIL_AND_GAS_MAP_PATH,
  PORTFOLIO_ANALYSIS_PATH,
  RESERVES_DEFINITION_PATH,
  VALUATIONS_PATH,
  VALUATIONS_V2_PATH,
  VIDEO_TUTORIALS_PATH,
} from '../../../utils/constants';

export const useLinks = () => {
  const { globalDownloads, valuationsV2 } = useFeatureFlags();
  const { hasTutorial, setTutorial } = useTutorialContext();
  const location = useLocation();
  const path = location.pathname;

  const dataDownloads = useMemo(() => {
    if (globalDownloads) {
      return {
        title: 'Data downloads',
        type: 'menu',
        menuItems: [
          {
            title: 'Country-specific',
            page: 'download',
            type: 'shiny',
          },
          {
            title: 'Global',
            link: GLOBAL_DOWNLOADS_PATH,
            type: 'link',
          },
        ],
      };
    }

    return {
      title: 'Data downloads',
      page: 'download',
      type: 'shiny',
    };
  }, [globalDownloads]);

  const links = useMemo(
    () => [
      {
        title: 'Mapping tools',
        type: 'menu',
        menuItems: [
          {
            title: 'Oil and gas map',
            link: OIL_AND_GAS_MAP_PATH,
            type: 'link',
          },
          {
            title: 'Heat maps',
            page: 'map',
            type: 'shiny',
          },
        ],
      },
      {
        title: 'Upstream assets',
        type: 'menu',
        menuItems: [
          {
            title: 'Valuations and reports',
            link: VALUATIONS_PATH,
            type: 'link',
          },
          ...(valuationsV2
            ? [
                {
                  title: 'Valuations v2',
                  link: VALUATIONS_V2_PATH,
                  type: 'link',
                },
              ]
            : []),
          {
            title: 'Technical analysis',
            page: 'fields',
            type: 'shiny',
          },
        ],
      },
      {
        title: 'Macro analysis',
        type: 'menu',
        menuItems: [
          {
            title: 'Country overviews',
            link: COUNTRY_OVERVIEWS_PATH,
            type: 'link',
          },
          {
            title: 'Portfolio analysis',
            link: PORTFOLIO_ANALYSIS_PATH,
            type: 'link',
          },
          {
            title: 'Greenhouse gas analytics',
            link: GHG_PATH,
            type: 'link',
          },
          {
            title: 'CCUS',
            link: CCUS_PATH,
            type: 'link',
          },
        ],
      },
      dataDownloads,
      {
        title: 'M&A analytics',
        type: 'menu',
        menuItems: [
          {
            title: 'M&A Transactions',
            type: 'divider',
          },
          {
            title: 'Overview',
            link: M_A_TRANSACTIONS_OVERVIEW_PATH,
            type: 'link',
          },
          {
            title: 'Transaction table',
            link: M_A_TRANSACTIONS_TABLE_PATH,
            type: 'link',
          },
          {
            title: 'Transaction analytics',
            link: M_A_TRANSACTIONS_ANALYTICS_PATH,
            type: 'link',
          },
          {
            title: 'Company analysis',
            link: M_A_TRANSACTIONS_COMPANY_ANALYSIS_PATH,
            type: 'link',
          },
          {
            title: 'M&A Opportunities',
            type: 'divider',
          },
          {
            title: 'Opportunities table',
            link: M_A_OPPORTUNITIES_TABLE_PATH,
            type: 'link',
          },
          {
            title: 'Opportunities analytics',
            link: M_A_OPPORTUNITIES_ANALYTICS_PATH,
            type: 'link',
          },
        ],
      },
      {
        title: 'Intelligence',
        link: 'https://welligence.com/intelligence',
        type: 'href',
      },
      {
        title: 'Help',
        type: 'menu',
        menuItems: [
          {
            title: 'Help',
            link: RESERVES_DEFINITION_PATH,
            type: 'link',
          },
          {
            title: 'Video Tutorials',
            link: VIDEO_TUTORIALS_PATH,
          },
          ...(hasTutorial
            ? [
                {
                  title: 'Page tutorial',
                  type: 'button',
                  onClick: () => {
                    gtag('event', ANALYTICS_EVENTS.event, {
                      event_category: 'walkthrough',
                      event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
                      event_label: 'start_walkthrough_menu_button',
                      userId: getUserId(),
                      page_location: path,
                    });
                    setTutorial(true);
                  },
                },
              ]
            : []),
        ],
      },
    ],
    [dataDownloads, valuationsV2, hasTutorial, path, setTutorial],
  );

  return links;
};
